/* Ayudate · Subida del comprobante de pago (/pagar)

   La ventana que abre el botón del correo de cobro. Reemplaza al "responda
   este correo con la captura adjunta": el estudiante paga por SINPE, entra
   acá con el enlace de su correo y sube la imagen. Eso deja el archivo en
   Drive y le manda al equipo un enlace para verlo y decidir en una sola
   ventana (ver subirComprobantePago y paginaRevisarPago en apps-script.gs).

   Sin login, igual que /calificar: lo que autoriza es el token del correo, y
   antes de mostrar el formulario se le pregunta al servidor si ese enlace
   todavía sirve — una reserva vencida, ya pagada o con el comprobante ya
   mandado tiene cada una su propia explicación, y ninguna es "elija una
   imagen y después le decimos".

   Las reglas del archivo son las MISMAS que las del comprobante de nota del
   tutor y la foto de perfil, y por eso salen de los mismos dos módulos
   (lib/tiposArchivo.js y lib/comprobante.js) en vez de reescribirse acá:
   JPG/PNG/WEBP, máximo 12 MB de entrada, y se reduce a 1600 px de lado antes
   de viajar. El servidor vuelve a revisar formato y peso. */

import React, { useEffect, useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";
import { AlertaArchivo } from "../componentes/Mensajes";
import { validarExtensionImagen, FORMATOS_IMAGEN_TEXTO } from "../lib/tiposArchivo";
import { prepararComprobante } from "../lib/comprobante";
import { colones } from "../lib/formato";

/* Mismos códigos que devuelve verificarTokenComprobante() en el servidor. */
const MENSAJE_ERROR_TOKEN = {
  MAL_FORMADO: "Este enlace no trae el código de su pago. Revise el correo que le enviamos y abra el enlace completo, o escríbanos por WhatsApp.",
  NO_ENCONTRADA: "No encontramos ninguna reserva con este enlace. Escríbanos por WhatsApp si cree que es un error.",
  YA_RECIBIDO: "Ya recibimos su comprobante y está en revisión. Le escribimos apenas quede confirmada la sesión — no hace falta subirlo de nuevo.",
  YA_PAGADA: "Esta tutoría ya está pagada y confirmada. Revise su correo: ahí le mandamos el enlace de la sala.",
  PAGO_RECHAZADO: "El comprobante de esta reserva se revisó y no se pudo dar por bueno, así que el horario quedó libre. Escríbanos por WhatsApp si cree que hay un error.",
  PLAZO_VENCIDO: "El plazo de 8 horas para pagar esta reserva ya venció y el horario se liberó. Puede volver a solicitar la tutoría desde la página.",
  NO_VIGENTE: "Esta reserva ya no está esperando pago. Escríbanos por WhatsApp si cree que es un error.",
};

/* Los que puede devolver el envío (ver subirComprobantePago). Los tres
   primeros son los mismos de arriba: entre que se abrió la ventana y se
   tocó el botón pudo vencer el plazo o entrar otra pestaña. */
const MENSAJE_ERROR_ENVIO = {
  YA_RECIBIDO: "Ya recibimos un comprobante para esta reserva y está en revisión.",
  PLAZO_VENCIDO: "El plazo de 8 horas venció mientras llenaba esto y el horario se liberó. Escríbanos por WhatsApp si ya hizo el SINPE.",
  NO_VIGENTE: "Esta reserva ya no está esperando pago. Recargue la página para ver cómo quedó.",
  FORMATO_NO_PERMITIDO: `Solo se aceptan imágenes en ${FORMATOS_IMAGEN_TEXTO}.`,
  ARCHIVO_PESADO: "La imagen pesa demasiado. Pruebe con otra.",
  ARCHIVO_INVALIDO: "No pudimos leer esa imagen. Pruebe con otra.",
  DRIVE_NO_DISPONIBLE: "No pudimos guardar su comprobante en este momento. Pruebe de nuevo en un minuto; si sigue fallando, escríbanos por WhatsApp.",
  DEMASIADAS_PETICIONES: "Hay otro envío de este mismo comprobante en curso. Espere unos segundos y recargue la página.",
};

export function PaginaSubirComprobante() {
  const token = new URLSearchParams(window.location.search).get("t") || "";

  const [verificando, setVerificando] = useState(true);
  const [cobro, setCobro] = useState(null);       // datos de la reserva, o null
  const [errorToken, setErrorToken] = useState("");

  const [archivo, setArchivo] = useState(null);   // dataURL ya preparada
  const [nombre, setNombre] = useState("");
  const [errorArchivo, setErrorArchivo] = useState("");
  const [preparando, setPreparando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setVerificando(false); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await api.verificarComprobantePago(token);
        if (!vivo) return;
        if (r.ok) setCobro(r);
        else setErrorToken(MENSAJE_ERROR_TOKEN[r.error] || MENSAJE_ERROR_TOKEN.NO_ENCONTRADA);
      } catch {
        if (vivo) setErrorToken("No pudimos verificar su enlace. Revise su conexión y pruebe de nuevo.");
      } finally {
        if (vivo) setVerificando(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  /* Se prepara al elegirla, no al enviar: así el aviso de "ese formato no
     sirve" sale mientras todavía está pensando en el archivo, y el botón de
     enviar ya tiene la imagen lista cuando lo toca. */
  const elegirArchivo = async (elegido) => {
    setError("");
    if (!elegido) { setArchivo(null); setNombre(""); return; }
    try {
      validarExtensionImagen(elegido);
    } catch (err) {
      setArchivo(null); setNombre(""); setErrorArchivo(err.message);
      return;
    }
    setPreparando(true); setErrorArchivo("");
    try {
      const dataUrl = await prepararComprobante(elegido);
      setArchivo(dataUrl); setNombre(elegido.name);
    } catch (err) {
      setArchivo(null); setNombre("");
      setErrorArchivo(err.message || "No pudimos leer esa imagen.");
    } finally {
      setPreparando(false);
    }
  };

  const enviar = async () => {
    if (!archivo || enviando) return;
    setEnviando(true); setError("");
    try {
      await api.subirComprobantePago(token, archivo);
      setListo(true);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      setError(MENSAJE_ERROR_ENVIO[codigo] || err.message
        || "No pudimos subir su comprobante. Pruebe de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="v2">
      <style>{ESTILOS_MINIMOS}</style>
      <div className="mini-wrap">
        <div className="logo" style={{ marginBottom: 28 }}><Marca /></div>

        {!token ? (
          <div className="mini-card">
            <h1>Enlace incompleto</h1>
            <p>
              Este enlace no trae el código de su pago. Revise el correo que le enviamos y{" "}
              <b>abra el enlace completo</b>, o escríbanos por WhatsApp.
            </p>
            <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Ir al inicio
            </a>
          </div>
        ) : verificando ? (
          <div className="mini-card"><p style={{ margin: 0 }}>Verificando su enlace…</p></div>
        ) : errorToken ? (
          <div className="mini-card">
            <h1>No pudimos abrir su pago</h1>
            <p>{errorToken}</p>
            <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Ir al inicio
            </a>
          </div>
        ) : listo ? (
          <div className="mini-card">
            <h1>Comprobante recibido</h1>
            <p>
              Ya lo tenemos y lo estamos revisando. Apenas quede verificado le llega un correo
              con la confirmación de la sesión y el enlace de su sala — no hace falta que haga
              nada más ni que responda ningún correo.
            </p>
            <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Volver al inicio
            </a>
          </div>
        ) : (
          <div className="mini-card">
            <h1>Suba su comprobante</h1>
            <p>
              {cobro.curso} con {cobro.tutorNombre} · {cobro.horas} h.
              Total a pagar: <b>{colones(cobro.total)}</b>.
            </p>
            <p className="ayuda" style={{ marginTop: 0 }}>{cobro.detalle}</p>

            <p>
              Haga el <b>SINPE Móvil</b> al <b>{cobro.sinpe.numero}</b>
              {cobro.sinpe.titular ? <> (a nombre de {cobro.sinpe.titular})</> : null} y suba
              acá la foto o captura del comprobante. Le quedan{" "}
              <b>{cobro.horasRestantes} horas</b> antes de que el horario se libere.
            </p>

            <label className="lb" htmlFor="comprobante">
              Comprobante · {FORMATOS_IMAGEN_TEXTO}
            </label>
            <input id="comprobante" className="in" type="file" accept="image/*"
              onChange={(e) => elegirArchivo(e.target.files?.[0] || null)} />
            {errorArchivo && <AlertaArchivo mensaje={errorArchivo} />}
            {preparando && <p className="ayuda">Preparando la imagen…</p>}
            {archivo && !preparando && (
              <>
                <p className="ayuda">Listo para enviar: {nombre}</p>
                <img src={archivo} alt="Vista previa de su comprobante"
                  style={{ display: "block", width: "100%", borderRadius: 12, marginTop: 8 }} />
              </>
            )}

            {error && <div className="error" role="alert" style={{ marginTop: 14 }}>{error}</div>}

            <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
              onClick={enviar} disabled={!archivo || preparando || enviando}>
              {enviando ? "Subiendo…" : "Enviar mi comprobante"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
